import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPen,
  faCheck,
  faXmark,
  faChartBar,
  faShareNodes,
} from '@fortawesome/free-solid-svg-icons';
import {
  useDashboard,
  useUpdateDashboard,
  useAddWidget,
  useUpdateWidget,
  useRemoveWidget,
  useBatchUpdatePositions,
} from './hooks/useDashboards';
import type {
  BIQueryError,
  WidgetType,
  DateRange,
  FilterCondition,
  BiWidget,
  DataConfig,
} from '../../types/bi';
import type { WidgetConfigUpdate } from './components/QueryDesigner';
import { resolveDrilldown } from './utils/drilldown';
import type { DrilldownFilter, DrilldownEvent } from './utils/drilldown';
import DashboardGrid from './components/DashboardGrid';
import DrilldownBreadcrumb from './components/DrilldownBreadcrumb';
import WidgetToolbar from './components/WidgetToolbar';
import DateRangePicker from './components/DateRangePicker';
import FilterBar from './components/FilterBar';
import QueryDesigner from './components/QueryDesigner';
import ShareDialog from './components/ShareDialog';
import ExportMenu from './components/ExportMenu';
import ScheduleDialog from './components/ScheduleDialog';
import AlertDialog from './components/AlertDialog';
import styles from './DashboardViewPage.module.css';

function getExplainDebugFlags() {
  const envEnabled = import.meta.env.VITE_BI_EXPLAIN_DEBUG === 'true';
  let queryValue: string | null = null;
  try {
    queryValue = new URLSearchParams(window.location.search).get('bi_explain');
  } catch {
    queryValue = null;
  }

  const available = envEnabled || queryValue !== null;
  const enabledByQuery = queryValue === '1';

  return {
    available,
    enabled: envEnabled || enabledByQuery,
  };
}

export default function DashboardViewPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: dashboard, isLoading, isError } = useDashboard(slug || '');
  const updateDashboard = useUpdateDashboard();
  const addWidgetMutation = useAddWidget(dashboard?.id || 0);
  const updateWidgetMutation = useUpdateWidget(dashboard?.id || 0);
  const removeWidgetMutation = useRemoveWidget(dashboard?.id || 0);
  const batchPositions = useBatchUpdatePositions(dashboard?.id || 0);

  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [globalFilters, setGlobalFilters] = useState<FilterCondition[]>([]);
  const [editingWidgetId, setEditingWidgetId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [drilldownFilters, setDrilldownFilters] = useState<DrilldownFilter[]>([]);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [alertWidget, setAlertWidget] = useState<{ dataConfig: DataConfig; title: string } | null>(
    null,
  );
  const [widgetQueryErrors, setWidgetQueryErrors] = useState<Record<number, BIQueryError | null>>(
    {},
  );
  const explainFlags = getExplainDebugFlags();
  const [explainDebugEnabled, setExplainDebugEnabled] = useState<boolean>(explainFlags.enabled);

  const canEdit =
    !dashboard?.user_permission ||
    dashboard.user_permission === 'edit' ||
    dashboard.user_permission === 'owner';

  /* ─── Handlers ─── */
  const handleAddWidget = useCallback(
    (type: WidgetType) => {
      addWidgetMutation.mutate({ widget_type: type });
    },
    [addWidgetMutation],
  );

  const handleSavePositions = useCallback(
    (positions: { id: number; x: number; y: number; w: number; h: number }[]) => {
      batchPositions.mutate(positions);
    },
    [batchPositions],
  );

  const handleConfigureWidget = useCallback((widget: BiWidget) => {
    setEditingWidgetId(widget.id);
  }, []);

  const handleRemoveWidget = useCallback(
    (widgetId: number) => {
      if (!window.confirm('Remove this widget?')) return;
      removeWidgetMutation.mutate(widgetId);
    },
    [removeWidgetMutation],
  );

  const handleRenameWidget = useCallback(
    (widgetId: number, newTitle: string) => {
      updateWidgetMutation.mutate({ widgetId, title: newTitle });
    },
    [updateWidgetMutation],
  );

  const handleDrillDown = useCallback(
    (widgetId: number, event: DrilldownEvent) => {
      const widget = dashboard?.widgets?.find((w) => w.id === widgetId);
      const action = resolveDrilldown(widget?.drilldown_config ?? null, event);
      if (!action) return;

      if (action.type === 'navigate') {
        navigate(action.url);
      } else {
        // Don't add duplicate filters for the same field
        setDrilldownFilters((prev) => {
          const existing = prev.findIndex((f) => f.field_key === action.filter.field_key);
          if (existing >= 0) {
            return prev.map((f, i) => (i === existing ? action.filter : f));
          }
          return [...prev, action.filter];
        });
      }
    },
    [dashboard, navigate],
  );

  const handleRemoveDrilldownFilter = useCallback((index: number) => {
    setDrilldownFilters((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleClearDrilldownFilters = useCallback(() => {
    setDrilldownFilters([]);
  }, []);

  const handleSaveQuery = useCallback(
    (update: WidgetConfigUpdate) => {
      if (editingWidgetId) {
        updateWidgetMutation.mutate({
          widgetId: editingWidgetId,
          data_config: update.data_config,
          semantic_intent: update.semantic_intent,
          projection_config: update.projection_config,
          annotations: update.annotations,
          conditional_formatting: update.conditional_formatting,
          drilldown_config: update.drilldown_config,
          style_config: update.style_config,
        });
      }
      setEditingWidgetId(null);
    },
    [editingWidgetId, updateWidgetMutation],
  );

  const handleWidgetQueryErrorChange = useCallback(
    (widgetId: number, error: BIQueryError | null) => {
      setWidgetQueryErrors((prev) => {
        const existing = prev[widgetId] || null;
        const existingCode = existing?.reasonCode || '';
        const nextCode = error?.reasonCode || '';
        const existingMsg = existing?.message || '';
        const nextMsg = error?.message || '';
        if (existingCode === nextCode && existingMsg === nextMsg) {
          return prev;
        }
        return { ...prev, [widgetId]: error };
      });
    },
    [],
  );

  const startEditTitle = useCallback(() => {
    if (!dashboard) return;
    setTitleDraft(dashboard.name);
    setEditingTitle(true);
  }, [dashboard]);

  const saveTitle = useCallback(() => {
    if (!dashboard || !titleDraft.trim()) return;
    updateDashboard.mutate({ id: dashboard.id, name: titleDraft.trim() });
    setEditingTitle(false);
  }, [dashboard, titleDraft, updateDashboard]);

  /* ─── Render ─── */
  if (isLoading)
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading dashboard…</div>
      </div>
    );
  if (isError || !dashboard) {
    return (
      <div className={styles.page}>
        <div className={styles.notFound}>
          <h2>Dashboard not found</h2>
          <button className={styles.backBtn} onClick={() => navigate('/bi')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to dashboards
          </button>
        </div>
      </div>
    );
  }

  const editingWidget = editingWidgetId
    ? dashboard.widgets?.find((w) => w.id === editingWidgetId)
    : null;

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.left}>
          <button className={styles.backBtn} onClick={() => navigate('/bi')}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          {editingTitle ? (
            <div className={styles.titleEdit}>
              <input
                className={styles.titleInput}
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveTitle();
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
                autoFocus
              />
              <button className={styles.titleSave} onClick={saveTitle}>
                <FontAwesomeIcon icon={faCheck} />
              </button>
              <button className={styles.titleCancel} onClick={() => setEditingTitle(false)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ) : (
            <h1 className={styles.title} onClick={canEdit ? startEditTitle : undefined}>
              {dashboard.name}
              {canEdit && <FontAwesomeIcon icon={faPen} className={styles.editIcon} />}
            </h1>
          )}
        </div>

        <div className={styles.right}>
          {explainFlags.available && (
            <label className={styles.debugToggle} title="Internal BI explain diagnostics">
              <input
                type="checkbox"
                checked={explainDebugEnabled}
                onChange={(e) => setExplainDebugEnabled(e.target.checked)}
              />
              Explain
            </label>
          )}
          <FilterBar filters={globalFilters} onChange={setGlobalFilters} />
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <ExportMenu
            dashboardId={dashboard.id}
            dashboardName={dashboard.name}
            onSchedule={() => setShowScheduleDialog(true)}
          />
          {canEdit && (
            <button
              className={styles.backBtn}
              onClick={() => setShowShareDialog(true)}
              title="Share dashboard"
            >
              <FontAwesomeIcon icon={faShareNodes} />
            </button>
          )}
          {canEdit && <WidgetToolbar onAddWidget={handleAddWidget} />}
        </div>
      </header>

      {/* Drill-down breadcrumb */}
      <DrilldownBreadcrumb
        filters={drilldownFilters}
        onRemove={handleRemoveDrilldownFilter}
        onClearAll={handleClearDrilldownFilters}
      />

      {/* Grid */}
      {!dashboard.widgets || dashboard.widgets.length === 0 ? (
        <div className={styles.emptyGrid}>
          <FontAwesomeIcon icon={faChartBar} className={styles.emptyIcon} />
          <h2>No widgets yet</h2>
          <p>
            Click <strong>Add Widget</strong> in the toolbar above to get started.
          </p>
        </div>
      ) : (
        <DashboardGrid
          widgets={dashboard.widgets}
          dateRange={dateRange}
          globalFilters={globalFilters}
          drilldownFilters={drilldownFilters}
          explainDebugEnabled={explainDebugEnabled}
          onSavePositions={handleSavePositions}
          onConfigureWidget={handleConfigureWidget}
          onRemoveWidget={handleRemoveWidget}
          onRenameWidget={handleRenameWidget}
          onDrillDown={handleDrillDown}
          onWidgetQueryErrorChange={handleWidgetQueryErrorChange}
          onCreateAlert={(widget) =>
            setAlertWidget({
              dataConfig: widget.data_config,
              title: widget.title || widget.widget_type,
            })
          }
          readOnly={!canEdit}
        />
      )}

      {/* Query designer slide-over */}
      {editingWidget && (
        <QueryDesigner
          widgetType={editingWidget.widget_type}
          validationError={widgetQueryErrors[editingWidget.id] || null}
          dataConfig={
            editingWidget.data_config || {
              source_table: '',
              dimensions: [],
              measures: [],
              filters: [],
              sort: [],
              limit: 1000,
            }
          }
          projectionConfig={editingWidget.projection_config}
          annotations={editingWidget.annotations}
          conditionalFormatting={editingWidget.conditional_formatting}
          drilldownConfig={editingWidget.drilldown_config}
          styleConfig={editingWidget.style_config}
          onSave={handleSaveQuery}
          onClose={() => setEditingWidgetId(null)}
        />
      )}

      {/* Share / Embed dialog */}
      {showShareDialog && (
        <ShareDialog
          dashboardId={dashboard.id}
          dashboardName={dashboard.name}
          onClose={() => setShowShareDialog(false)}
        />
      )}

      {/* Schedule dialog */}
      {showScheduleDialog && (
        <ScheduleDialog
          dashboardId={dashboard.id}
          dashboardName={dashboard.name}
          onClose={() => setShowScheduleDialog(false)}
        />
      )}

      {/* Alert dialog */}
      {alertWidget && (
        <AlertDialog
          dataConfig={alertWidget.dataConfig}
          widgetTitle={alertWidget.title}
          onClose={() => setAlertWidget(null)}
        />
      )}
    </div>
  );
}
